<template>
  <div>
    <el-form
      class="campaign-form"
      :model="campaignForm"
      :rules="rules"
      label-width="190px"
      label-position="left"
      ref="campaignForm"
    >
      <h3>Details</h3>
      <p v-if="!isEditing">
        Let's first get some details on what we want to call this new campaign, we need to know when
        this campaign is starting and ending as well.
      </p>
      <p class="small" v-if="!isEditing">
        The campaign type is very important, getting this right ensures the best reporting
        available. This cannot be changed once the campaign is created.
      </p>
      <p class="small" v-if="isEditing">
        Due to interaction processing and analytics the campaign type and keyword cannot be changed.
      </p>

      <el-form-item label="Title" prop="campaignTitle">
        <el-input v-model="campaignForm.campaignTitle" />
      </el-form-item>

      <el-form-item label="Perpetual Campaign" prop="perpetual">
        <el-switch @change="clearCampaignDates" v-model="campaignForm.perpetual" />
        <p class="small">Select to setup a permanent campaign without an end date.</p>
      </el-form-item>

      <el-form-item v-if="campaignForm.perpetual" label="Start Date" prop="campaignDates">
        <el-date-picker
          style="width: 100%"
          v-model="campaignForm.campaignDates[0]"
          type="datetime"
          @change="handleChangeCampaignDate"
        ></el-date-picker>
      </el-form-item>

      <el-form-item v-else label="Dates" prop="campaignDates">
        <el-date-picker
          style="width: 100%"
          v-model="campaignForm.campaignDates"
          type="datetimerange"
          range-separator="To"
        ></el-date-picker>
      </el-form-item>

      <el-form-item label="Type" prop="campaignType">
        <el-select
          class="full-width"
          v-model="campaignForm.campaignType"
          :value="null"
          :disabled="isEditing"
        >
          <el-option
            v-for="item in campaignTypeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item
        label="Winning Disqualification Duration"
        prop="campaignWinningQualificationDuration"
      >
        <el-select
          class="duration-selectors"
          v-model="campaignForm.duration_days"
          :value="null"
          filterable
          placeholder="days"
        >
          <el-option
            v-for="(item, index) in range(0, 100)"
            :key="index"
            :label="item + ' days'"
            :value="item"
          />
        </el-select>
      </el-form-item>

      <div v-if="pollCampaignTypes.includes(campaignForm.campaignType)">
        <div class="hr-line-dashed"></div>
        <h3>Poll Details</h3>

        <el-form-item label="Question" prop="question">
          <el-input v-model="campaignForm.pollQuestion" />
        </el-form-item>

        <div class="poll-choice-container" style="width: 95%">
          <h4>Choices</h4>
          <p>Specify poll choices along with possible matches for each choice</p>
          <poll-choice-input
            v-for="(choice, idx) in campaignForm.pollChoices"
            :choice="choice"
            :idx="idx"
            @onRemoveChoice="handleChoiceRemove"
            :key="idx"
            :disabled="isEditing"
          />
        </div>

        <div>
          <el-button
            icon="fa fa-plus"
            class="pull-right"
            @click="handleAddPollChoice"
            v-if="!isEditing"
            >Add Choice</el-button
          >
        </div>
        <div class="clearfix"></div>
      </div>

      <div class="hr-line-dashed"></div>
      <h3>Channels</h3>
      <p>These are the means by which interactions come into or get sent out of a campaign.</p>
      <el-form-item label="Keyword">
        <el-tag
          :key="tag"
          style="margin-left:2px;"
          v-for="tag in campaignForm.channelKeyword"
          :closable="!isEditing"
          :disable-transitions="false"
          @close="handleClose(tag)"
          >{{ tag }}</el-tag
        >

        <el-input
          class="input-new-tag"
          v-if="keywordInputVisible"
          :disabled="isEditing"
          v-model="keyword"
          ref="saveTagInput"
          @keyup.enter.native="handleInputConfirm"
          @blur="handleInputConfirm"
        ></el-input>
        <span v-if="!isEditing">
          <el-button size="small" @click="showInput">+ Add keyword</el-button>
          <el-tooltip content="Keywords cannot be added when campaign is live" placement="bottom">
            <i class="fa fa-info-circle fa-lg"></i>
          </el-tooltip>
          <p class="small">Press enter key after each keyword added.</p>
        </span>
        <p v-else>Keywords cannot be added when campaign is live</p>
      </el-form-item>

      <el-form-item
        v-if="smsChannelSelected && canEncryptOriginators"
        label="Encrypt Originator"
        prop="encryptOriginator"
      >
        <el-switch v-model="encryptOriginator" />
      </el-form-item>

      <el-form-item v-if="smsChannelSelected" label="Reply Message" prop="replyMessage">
        <el-input v-model="replyMessage" type="textarea" class="full-width" />
        <div>{{ smsCount }} (chars left / messages)</div>
        <div class="small" v-if="encryptOriginator">
          Please replace the part of the reply would like encrypted with <strong>{hashid}</strong>
          <br />
          <i
            >e.g. Thank you for entering, please visit https://example.com/enter?uid=<strong
              >{hashid}</strong
            ></i
          >
        </div>
      </el-form-item>

      <el-dialog
        title="SMS Credentials"
        :visible.sync="showSmsCredentialsDialog"
        width="30%"
        :show-close="false"
        append-to-body
      >
        <el-form
          :model="smsReplyCredentialsForm"
          :rules="smsCredentialsrules"
          ref="smsReplyCredentialsForm"
        >
          <h3>Details</h3>
          <p>
            Kindly provide us with the username and password for your sms line. These detials are
            vital for us to pull in your interactions.
          </p>
          <el-form-item label="Username" prop="username">
            <el-input
              class="full-width"
              placeholder="Username"
              v-model="smsReplyCredentialsForm.username"
            />
          </el-form-item>
          <el-form-item label="Password" prop="password">
            <el-input
              class="full-width"
              placeholder="password"
              v-model="smsReplyCredentialsForm.password"
            />
          </el-form-item>
          <el-button type="primary" @click="handleConfirmSmsCredential">Confirm</el-button>
        </el-form>
      </el-dialog>

      <channel-select
        :createChannels="this.channelList"
        :selectedChannels="campaignForm.channels"
        @onSelectedChannelsChanged="handleSelectedChannelsChanged"
      />

      <h4>Create a new channel</h4>
      <p>The channel you're looking for not listed above, create a new one below.</p>
      <el-button-group>
        <el-button @click="handleRequestTwitterAuth">
          <social-icon class="fa fa-fw" :type="'Twitter'" />Twitter
        </el-button>
        <el-button @click="handleRequestFacebookAuth">
          <social-icon class="fa fa-fw" :type="'Facebook'" />Facebook
        </el-button>
        <el-button @click="handleRequestInstagramAuth">
          <social-icon class="fa fa-fw" :type="'Instagram'" />Instagram
        </el-button>
      </el-button-group>

      <div class="footer">
        <div class="pull-right">
          <el-button type="default" @click="handleCancel">Cancel</el-button>
          <el-button type="primary" @click="handleFormSave" :disabled="loading">
            <i class="fa fa-spinner fa-spin" v-if="loading"></i>
            Save
          </el-button>
        </div>
      </div>
    </el-form>
  </div>
</template>

<script>
import TaxonomySelect from '@/components/taxonomies/TaxonomySelect';
import ChannelSelect from '@/components/channel/ChannelSelect';
import SocialIcon from '@/components/common/Icons/SocialIcon';
import PollChoiceInput from '@/components/polls/PollChoiceInput';
import gql from 'graphql-tag';
import moment from 'moment';
import momentDurationFormatPlugin from 'moment-duration-format';

export default {
  name: 'CampaignForm',

  components: {
    ChannelSelect,
    TaxonomySelect,
    SocialIcon,
    PollChoiceInput,
  },

  props: {
    channels: {
      type: Array,
      default: () => [],
    },
    plugins: {
      type: Array,
      default: () => [],
    },
    createChannels: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    campaignToEdit: {
      type: Object,
      required: false,
    },
    isEditing: {
      type: Boolean,
      required: false,
    },
  },

  data() {
    const validateReplyMessage = (rule, value, callback) => {
      if (this.encryptOriginator) {
        const vars = this.replyMessage.match(/{\s*[\w.]+\s*}/g);
        if (vars && vars.length) {
          callback();
        } else {
          callback(new Error('Please add a hash id to your message'));
        }
      } else {
        callback();
      }
    };

    return {
      campaignForm: {
        campaignTitle: '',
        campaignDates: [],
        campaignType: null,
        pollQuestion: '',
        perpetual: false,
        pollChoices: [{ choice: '', matches: [] }],
        channelKeyword: [],
        channels: [],
        campaignWinningQualificationDuration: '',
        duration_days: 0,
        duration_hours: 0,
        duration_minutes: 0,
        duration_seconds: 0,
      },
      nextTick: '',
      keywordInputVisible: false,
      keyword: '',
      smsReplyCredentialsForm: {
        username: '',
        password: '',
      },
      showSmsCredentialsDialog: false,
      replyBoundSMSChannels: ['SMS', 'Dms', 'Infobip', 'Mtech', 'CellFind'],
      smsReplyCredentialsRequired: ['Dms', 'Infobip', 'Mtech'],
      // prettier-ignore
      smsCredentialsrules: {
        username: [
          { required: true, message: 'Please input the username' },
        ],
        password: [
          { required: true, message: 'Please input your password' },
        ],
      },
      rules: {
        campaignTitle: [{ required: true, message: 'Please input the name of your campaign' }],
        campaignDates: [
          { required: true, message: 'Please select the date range for your campaign' },
        ],
        campaignType: [
          { required: true, message: 'Please select the type of campaign you wish to run' },
        ],
        pollQuestion: [{ required: true, message: 'Poll question is required.' }],
        replyMessage: [{ validator: validateReplyMessage, trigger: 'change' }],
      },
      campaignTypeOptions: [
        { label: 'Message Board', value: 'MESSAGE_BOARD' },
        { label: 'Competition', value: 'COMPETITION' },
        { label: 'Poll', value: 'POLL' },
        { label: 'Survey', value: 'SURVEY' },
        { label: 'Instant Poll', value: 'INSTANT_POLL' },
      ],
      campaign: {
        details: {},
        advertiser: {
          category: null,
          prizeCategory: null,
        },
        channels: [],
        keyword: '',
        perpetual: false,
      },
      showReplyMessageBox: false,
      replyMessage: '',
      encryptOriginator: false,
      pollCampaignTypes: ['POLL', 'INSTANT_POLL'],
    };
  },
  apollo: {
    channelList: {
      query: gql`
        {
          channelList(live: true) {
            id
            handle
            channelType
            channelTypeTitle
          }
        }
      `,
    },
  },

  computed: {
    canEncryptOriginators() {
      return this.plugins
        .map(plugin => plugin.plugin_route)
        .includes('app.campaigns.encrypt_originators');
    },
    smsCount() {
      const maxLength = 160;
      const messCount = Math.floor(this.replyMessage.length / maxLength) + 1;
      const charCount = maxLength - (this.replyMessage.length - maxLength * (messCount - 1));
      return `${charCount}/${messCount}`;
    },
    smsChannelSelected() {
      const selectedSMSChannels = this.channels
        .filter(channel => this.campaignForm.channels.includes(String(channel.id)))
        .filter(channel => this.replyBoundSMSChannels.includes(channel.channelTypeTitle));
      return selectedSMSChannels.length > 0;
    },
  },

  methods: {
    handleClose(tag) {
      this.campaignForm.channelKeyword.splice(this.campaignForm.channelKeyword.indexOf(tag), 1);
    },
    handleChangeCampaignDate(startDate) {
      this.campaignForm.campaignDates = [startDate, moment(startDate).add(100, 'years')];
    },
    clearCampaignDates() {
      this.campaignForm.campaignDates = [];
    },
    showInput() {
      this.keywordInputVisible = true;
      this.$nextTick(tick => {
        this.nextTick = tick;
        this.$refs.saveTagInput.$refs.input.focus();
      });
    },
    handleInputConfirm() {
      const keyword = this.keyword;
      if (keyword) {
        this.campaignForm.channelKeyword.push(keyword);
      }
      this.keywordInputVisible = false;
      this.keyword = '';
    },
    range(start, end) {
      return Array(end - start + 1)
        .fill()
        .map((_, idx) => start + idx);
    },
    handleSelectedChannelsChanged(selectedChannels) {
      this.campaignForm.channels = selectedChannels;
      this.showSmsCredentialsDialog =
        this.channels
          .filter(channel => this.campaignForm.channels.includes(String(channel.id)))
          .filter(channel => this.smsReplyCredentialsRequired.includes(channel.channelTypeTitle))
          .length > 0;
    },

    handleRequestTwitterAuth() {
      this.$emit('onRequestTwitterAuth');
    },

    handleRequestInstagramAuth() {
      this.$emit('onRequestInstagramAuth');
    },

    handleRequestFacebookAuth() {
      this.$emit('onRequestFacebookAuth');
    },

    handleRequestEmailChannelCreate() {
      this.$emit('onRequestEmailChannelCreate');
    },

    handleRequestWhatsappChannelCreate() {
      this.$emit('onRequestWhatsappChannelCreate');
    },

    handleCancel() {
      this.$emit('onFormCancel');
    },

    handleAddPollChoice() {
      this.campaignForm.pollChoices.push({ choice: '', matches: [] });
    },

    handleChoiceRemove(idx) {
      this.campaignForm.pollChoices.splice(idx, 1);
    },

    handleFormSave() {
      this.$refs.campaignForm.validate(valid => {
        if (valid) {
          return this.saveForm();
        }

        return false;
      });
    },
    handleConfirmSmsCredential() {
      this.$refs.smsReplyCredentialsForm.validate(valid => {
        if (valid) {
          this.showSmsCredentialsDialog = false;
          return true;
        }
        return false;
      });
    },

    saveForm() {
      let winningDisqualificationInMilliSeconds = this.convertToMilliseconds();
      if (winningDisqualificationInMilliSeconds === 0) winningDisqualificationInMilliSeconds = null;

      const campaignData = {
        id: this.isEditing ? parseInt(this.campaignToEdit.id, 10) : null,
        title: this.campaignForm.campaignTitle,
        startDate: this.campaignForm.campaignDates[0],
        endDate: this.campaignForm.campaignDates[1],
        campaignType: this.campaignForm.campaignType,
        winningDisqualificationDuration: winningDisqualificationInMilliSeconds,
        default: false,
        perpetual: this.campaignForm.perpetual,
        live: true,
      };

      const pollData = {
        question: this.campaignForm.pollQuestion,
        choices: this.campaignForm.pollChoices,
      };

      const channelData = {
        keywords: this.getKeywords(),
        channels: this.campaignForm.channels,
      };

      const replyMessageData = {
        text: this.replyMessage,
        encryptOriginator: this.encryptOriginator,
      };

      const replyMessageCredentialsData = {
        username: this.smsReplyCredentialsForm.username,
        password: this.smsReplyCredentialsForm.password,
      };

      this.$emit('onFormSave', {
        campaignData,
        channelData,
        pollData,
        replyMessageData,
        isEditing: this.isEditing,
        replyMessageCredentialsData,
      });
    },
    getKeywords() {
      return this.campaignForm.channelKeyword.length
        ? this.campaignForm.channelKeyword
        : [this.keyword];
    },
    convertToMilliseconds() {
      let valueInMilliseconds = 0;
      valueInMilliseconds = moment
        .duration({
          seconds:
            this.campaignForm.duration_seconds !== undefined
              ? this.campaignForm.duration_seconds.toString().replace(' seconds', '')
              : '',
          minutes:
            this.campaignForm.duration_minutes !== undefined
              ? this.campaignForm.duration_minutes.toString().replace(' minutes', '')
              : '',
          hours:
            this.campaignForm.duration_hours !== undefined
              ? this.campaignForm.duration_hours.toString().replace(' hours', '')
              : '',
          days:
            this.campaignForm.duration_days !== undefined
              ? this.campaignForm.duration_days.toString().replace(' days', '')
              : '',
        })
        .asMilliseconds();
      return valueInMilliseconds;
    },
    convertMillisecondsToMomentDuration(milliseconds) {
      moment.duration.format = momentDurationFormatPlugin;
      const duration = moment.duration(milliseconds, 'milliseconds').format('d h:m:s');
      if (duration === '0') {
        return {
          duration_days: '90',
          duration_hours: '0',
          duration_minutes: '0',
          duration_seconds: '0',
        };
      }
      return {
        duration_days: `${duration.split(' ')[0]} days`,
        duration_hours: `${duration.split(' ')[1].split(':')[0]} hours`,
        duration_minutes: `${duration.split(' ')[1].split(':')[1]} minutes`,
        duration_seconds: `${duration.split(' ')[1].split(':')[2]} seconds`,
      };
    },
    /**
     * Campaign Edit methods.
     * Map campaign details to campaignForm fields.
     */
    mapCampaignDetails() {
      const campaign = this.campaignToEdit;
      const durationMilliseconds = campaign.winningDisqualificationDuration;
      this.campaignForm.campaignType = campaign.campaignType;
      this.campaignForm.campaignTitle = campaign.title;
      this.campaignForm.campaignDates = [campaign.startDate, campaign.endDate];
      this.campaignForm.duration_days = this.convertMillisecondsToMomentDuration(
        durationMilliseconds,
      ).duration_days;
      this.campaignForm.duration_hours = this.convertMillisecondsToMomentDuration(
        durationMilliseconds,
      ).duration_hours;
      this.campaignForm.duration_minutes = this.convertMillisecondsToMomentDuration(
        durationMilliseconds,
      ).duration_minutes;
      this.campaignForm.duration_seconds = this.convertMillisecondsToMomentDuration(
        durationMilliseconds,
      ).duration_seconds;

      if (campaign.replymessage && campaign.replymessage.text) {
        this.replyMessage = campaign.replymessage.text;
        this.encryptOriginator = campaign.replymessage.encryptOriginator;
      }

      if (campaign.linkedcampaignchannelSet && campaign.linkedcampaignchannelSet.length) {
        this.mapLinkedCampaignChannels(campaign.linkedcampaignchannelSet);
      }

      if (this.pollCampaignTypes.includes(campaign.campaignType) && campaign.pollSet.length) {
        this.mapCampaignPollDetails(campaign.pollSet[0]);
      }
    },

    /**
     * Map Poll details to campaignForm fields.
     * */
    mapCampaignPollDetails(pollSet) {
      this.campaignForm.pollQuestion = pollSet.question;
      this.campaignForm.pollChoices = pollSet.pollchoiceSet.map(({ choice, matches }) => ({
        choice,
        matches,
      }));
    },

    /**
     * Map campaign channels to campaignForm channel input.
     * */
    mapLinkedCampaignChannels(linkedCampaignChannels) {
      const keywords = linkedCampaignChannels.map(x => x.keyword);
      this.campaignForm.channelKeyword = keywords.filter(
        (item, idx) => keywords.indexOf(item) === idx,
      );
      this.campaignForm.channels = linkedCampaignChannels.map(x => x.channel.id);
    },
  },

  /**
   * Doing this in mounted hook since a watcher will not fire correctly
   * because the prop is being passed down from the parent.
   * */
  mounted() {
    if (this.isEditing) {
      this.mapCampaignDetails();
    } else if (this.$refs.campaignForm && this.$refs.campaignForm.resetFields) {
      this.$refs.campaignForm.resetFields();
    }
  },
};
</script>

<style scoped>
.campaign-form {
  margin-bottom: 40px;
}
.keyword-input {
  max-width: 350px;
}
.duration-selectors {
  width: 20%;
}
</style>



// WEBPACK FOOTER //
// CampaignForm.vue?ea035b74